import {
  BENEFIT_PERSONAL_BRONZE,
  BENEFIT_PERSONAL_GOLD,
  BENEFIT_PERSONAL_SILVER,
  BENEFIT_POWERBOOST_BRONZE,
  BENEFIT_POWERBOOST_GOLD,
  BENEFIT_POWERBOOST_SILVER,
  BENEFIT_PROTRAINING_BRONZE,
  BENEFIT_PROTRAINING_GOLD,
  BENEFIT_PROTRAINING_SILVER,
  CHAMPIONSHIP_POINTS,
  ENERGY_DRINK,
  EVENT_POINTS,
  EXPERIENCE,
  MONEY,
  PRESTIGE,
  RANKING_POINTS,
  RESEARCH_POINTS,
  TOURNAMENT_TITLES,
  TRAINING_POINTS,
  TROPHY_BRONZE,
  TROPHY_GOLD,
  TROPHY_SILVER,
  UNIVERSAL_TRAINING_POINTS,
  WILD_CARD,
  WON_MATCHES,
  TOURNAMENT_POINTS,
  MULLIGANS,
  AVATAR,
  AVATAR_BG,
  TOURNAMENT_POINTS_DISCIPLINE_2,
} from '@/globalVariables'
import { replacePlaceholder, replaceUntranslatablePlaceholder, translate } from '@/plugins'

export const translateIconName = (
  name: string,
  grandPrizeRarity: string = '',
  benefitType: string = '',
  benefitLevel: number = 0,
): string => {
  if (!name) return ''

  const GP_TOOLTIP_MAPPING = {
    'gp-basic': 'grandPrize.tierBasic',
    'gp-bronze': 'grandPrize.tierBronze',
    'gp-silver': 'grandPrize.tierSilver',
    'gp-gold': 'grandPrize.tierGold',
    'gp-diamond': 'grandPrize.tierDiamond',
  }

  // handle special cases:

  // Normalize the prefixes of Grand Prizes
  if (name.includes('grand_prize-')) name = name.replace('grand_prize-', 'gp-')

  // common name for all gallery pictures
  if (name.includes('gallery_picture')) return translate('rewards.gallery_picture')
  // kept in case someone changes their mind
  // if (name.includes('gallery_picture')) {
  //   const pictureNumber = name.slice(-3)
  //   return translate(`gallery.picture${pictureNumber}`)
  // }

  // benefits could be translated this way instead
  // if (name === 'benefit') return translate(`rewards.${reward.type}_${reward.level}`)

  switch (name) {
    case MONEY:
      return translate('map.money')

    case WILD_CARD:
      return translate('parameters.wild_card')

    case ENERGY_DRINK:
      return translate('parameters.energy_drink')

    case RESEARCH_POINTS:
      return translate('research.researchPoints')

    case RANKING_POINTS.toLowerCase() || RANKING_POINTS:
      return translate('rankingsGroup.rankingPoints')

    case UNIVERSAL_TRAINING_POINTS.toLowerCase():
      return translate('map.UNIVERSAL_TRAINING_POINTS')

    case TRAINING_POINTS.toLowerCase():
      return translate('rewards.training_points')

    case EXPERIENCE:
      return translate('common.experiences')

    case PRESTIGE:
      return translate('rankingsGroup.prestige')

    case TROPHY_BRONZE:
      return translate('rankingsGroup.trophyBronze')

    case TROPHY_SILVER:
      return translate('rankingsGroup.trophySilver')

    case TROPHY_GOLD:
      return translate('rankingsGroup.trophyGold')

    case CHAMPIONSHIP_POINTS:
      return translate('common.championshipPoints')

    case 'trophy-gold':
      return translate('event.tournament')

    case TOURNAMENT_TITLES:
      return translate('club.tournamentTitles')

    case BENEFIT_POWERBOOST_BRONZE:
      return 'PowerBoost 15'

    case BENEFIT_POWERBOOST_SILVER:
      return 'PowerBoost 30'

    case BENEFIT_POWERBOOST_GOLD:
      return 'PowerBoost 60'

    case BENEFIT_PERSONAL_BRONZE:
      return 'Personal Sponsor 10'

    case BENEFIT_PERSONAL_SILVER:
      return 'Personal Sponsor 25'

    case BENEFIT_PERSONAL_GOLD:
      return 'Personal Sponsor 60'

    case BENEFIT_PROTRAINING_BRONZE:
      return 'ProTraining 10'

    case BENEFIT_PROTRAINING_SILVER:
      return 'ProTraining 25'

    case BENEFIT_PROTRAINING_GOLD:
      return 'ProTraining 60'

    case 'benefit':
      if (!benefitType || !benefitLevel) return translate('rewards.' + name)
      return translate(`rewards.${benefitType}_${benefitLevel}`)

    case WON_MATCHES:
    case 'first-place':
    case 'won_matches':
      return translate('grandPrize.duelsWon')

    case EVENT_POINTS:
      return translate('event.eventPoints')

    case 'event_points_multiplier':
      return translate('event.eventPointsMultiplier')

    case 'activity_coins':
      return translate('boardGames.activity_coins')

    case 'dice_7_12':
    case 'bonus_better_dice':
      return translate('boardGames.dice_7_12')

    case 'double_reward':
      return translate('boardGames.double_reward')

    case 'move_one_forward':
    case 'bonus_move_forward':
      return translate('boardGames.move_one_forward')

    case 'glacial_dice_of_burning':
      return translate('boardGames.glacial_dice_of_burning')

    case 'armor_of_ice_and_fire':
      return translate('boardGames.armor_of_ice_and_fire')

    case 'medals':
      return translate('event.medals')

    case 'masters_playoff_points':
      return translate('event.mastersPoints')

    case 'gp-basic':
    case 'gp-bronze':
    case 'gp-silver':
    case 'gp-gold':
    case 'gp-diamond':
      return replaceUntranslatablePlaceholder(translate(GP_TOOLTIP_MAPPING[name]), '{grandprize}')

    case 'grand_prize':
      if (!grandPrizeRarity) return translate('rewards.' + name)
      return replaceUntranslatablePlaceholder(
        translate(`rewards.${name}_${grandPrizeRarity}`),
        '{grandprize}',
      )

    case 'star-full': {
      return translate('event.eventTrainingStars')
    }
    case 'multiplier_5': {
      return translate('premiumGroup.wsm_season_pass_1_content_multiplier_5_bonus')
    }
    case 'target': {
      return translate('mechanics.map_items')
    }

    case 'piggy_bank_universal_training_points':
    case 'piggy_bank_gems':
    case 'piggy_bank_money':
      return translate('piggyBank.piggyBank')

    case TOURNAMENT_POINTS:
      return translate('tournaments.tournamentPoints')
    case MULLIGANS:
      return translate('tournaments.mulligan')

    case 'gunpowder': {
      return translate('event.gunpowder')
    }

    case 'fortune-basket-token':
    case 'fortune_basket_token':
    case 'fortune-festival-basket-token': {
      return translate('event.tokens')
    }

    case 'double-reward':
    case 'fortune_basket_double': {
      return translate('event.doubleReward')
    }

    case 'triple-reward':
    case 'fortune_basket_triple': {
      return translate('event.tripleReward')
    }

    case 'refresh': {
      return translate('event.refresh')
    }

    case 'fortune_basket_shuffle': {
      return translate('event.newPatch')
    }

    case 'league_points': {
      return translate('event.leaguePoints')
    }

    case AVATAR: {
      return translate('premiumGroup.avatar')
    }

    case AVATAR_BG: {
      return translate('premiumGroup.avatarBackground')
    }

    case TOURNAMENT_POINTS_DISCIPLINE_2: {
      return replacePlaceholder(translate('tournaments.specialPoints'), '{partner}', 'IBU')
    }

    case 'gallery_pack_regular':
    case 'gallery-pack-regular': {
      return replacePlaceholder(
        translate('gallery.typePack'),
        '{type}',
        translate('gallery.regularPack'),
      )
    }

    case 'gallery_pack_premium':
    case 'gallery-pack-premium': {
      return replacePlaceholder(
        translate('gallery.typePack'),
        '{type}',
        translate('gallery.premiumPack'),
      )
    }

    case 'gallery_pack_jumbo':
    case 'gallery-pack-jumbo': {
      return replacePlaceholder(
        translate('gallery.typePack'),
        '{type}',
        translate('gallery.jumboPack'),
      )
    }

    case 'puzzle-common':
    case 'gallery_pieces_common': {
      return translate('gallery.commonPiece')
    }

    case 'puzzle-rare':
    case 'gallery_pieces_rare': {
      return translate('gallery.rarePiece')
    }

    case 'puzzle-epic':
    case 'gallery_pieces_epic': {
      return translate('gallery.epicPiece')
    }

    default:
      if (name.includes('benefit-')) {
        const number = name.substring(name.indexOf('-') + 1)
        return translate(`rewards.benefit_${number}`)
      }
      return translate('map.' + name) || translate('rewards.' + name) || name
  }
}
